// FirebaseExporterSection.js - Restored with all functionalities
import React, { useState, useEffect } from 'react';
import { Save, Database, Link, Loader, Check, AlertCircle, Copy, History, QrCode } from 'lucide-react';
import { saveItineraryToStorage, getItineraryPublicUrl, checkExistingItinerary } from './../utils/storageUtils';
import { getPointType } from './../utils/utils';
import { getCountryCode } from './../utils/countryUtils';
import { getStoragePathComponents } from './../utils/filenameUtils';
import FeatureGuard from '../../FeatureGuard';
import { useAccessControl } from '../../../contexts/AccessControlContext';

export const FirebaseExporterSection = ({ itinerary, setItinerary, onStatusChange }) => {
  const { getFieldAccess } = useAccessControl();

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success', 'error', null
  const [message, setMessage] = useState('');
  const [showUrlDialog, setShowUrlDialog] = useState(false);
  const [showSqlDialog, setShowSqlDialog] = useState(false);
  const [publicUrl, setPublicUrl] = useState('');
  const [sqlCommand, setSqlCommand] = useState('');
  const [copied, setCopied] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [previousVersions, setPreviousVersions] = useState([]);
  const [isCheckingPrevious, setIsCheckingPrevious] = useState(false);

  useEffect(() => {
    // Check if this itinerary might exist in Firebase when metadata changes
    if (itinerary.metadata.country && itinerary.metadata.name) {
      checkForExistingItinerary();
    }
  }, [itinerary.metadata.country, itinerary.metadata.name]);

  const checkForExistingItinerary = async () => {
    if (!itinerary.metadata.country || !itinerary.metadata.name) return;
    
    setIsCheckingPrevious(true);
    try {
      const data = await checkExistingItinerary(itinerary.metadata.country, itinerary.metadata.name);
      setPreviousVersions(data.versions || []);
      setIsUpdateMode(Array.isArray(data.versions) && data.versions.length > 0);
    } catch (error) {
      console.error('Error checking for existing itinerary:', error);
    } finally {
      setIsCheckingPrevious(false);
    }
  };

  const validateMetadata = () => {
    const metadata = itinerary.metadata;
    const requiredFields = [
      { field: 'name', label: 'Itinerary Name' },
      { field: 'theme', label: 'Theme' },
      { field: 'country', label: 'Country' },
      { field: 'city', label: 'City' },
      { field: 'duration', label: 'Duration' },
      { field: 'difficulty', label: 'Difficulty' },
      { field: 'description', label: 'Description' },
      { field: 'thumbnailUrl', label: 'Thumbnail Image URL' }
    ];
    
    if (!metadata.coordinates?.latitude || !metadata.coordinates?.longitude) {
      return 'Location Coordinates (Latitude and Longitude) are required';
    }
    
    if (!metadata.categories || metadata.categories.length === 0 || 
        (metadata.categories.length === 1 && metadata.categories[0] === '')) {
      return 'Categories are required';
    }
    
    if (!metadata.tags || metadata.tags.length === 0 || 
        (metadata.tags.length === 1 && metadata.tags[0] === '')) {
      return 'Tags are required';
    }
    
    for (const { field, label } of requiredFields) {
      if (!metadata[field]) {
        return `${label} is required`;
      }
    }
    
    return null; // No errors
  };

  const handleSaveToFirebase = async (forceUpdate = false) => {
    const validationError = validateMetadata();
    if (validationError) {
      setStatus('error');
      setMessage(validationError);
      return;
    }

    setIsLoading(true);
    setStatus(null);
    setMessage('');

    try {
      // Ensure every point has required fields before saving
      const sanitizedItinerary = {
        ...itinerary,
        points: itinerary.points.map(point => ({
          ...point,
          tier: point.tier || 'free',
          language: point.language || itinerary.metadata.language || 'en',
        }))
      };

      const result = await saveItineraryToStorage(sanitizedItinerary, {
        ...itinerary.metadata,
        has_qr_codes: itinerary.metadata.has_qr_codes ?? false,
        forceUpdate: forceUpdate || isUpdateMode
      });
      
      setStatus('success');
      setMessage(`Itinerary ${isUpdateMode ? 'updated' : 'saved'} successfully`);
      setPublicUrl(result.fileUrl);
      
      if (onStatusChange) {
        onStatusChange('success', `Itinerary ${isUpdateMode ? 'updated' : 'saved'} successfully`);
      }
    } catch (error) {
      setStatus('error');
      setMessage(`Error: ${error.message}`);
      
      if (onStatusChange) {
        onStatusChange('error', error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const showPublicUrl = () => {
    try {
      if (publicUrl) {
        setShowUrlDialog(true);
      } else {
        const url = getItineraryPublicUrl(itinerary.metadata.country, itinerary.metadata.name, itinerary.metadata.filename, itinerary.metadata.language);
        setPublicUrl(url);
        setShowUrlDialog(true);
      }
    } catch (error) {
      setStatus('error');
      setMessage(`Error getting URL: ${error.message}`);
    }
  };

  // Helper Functions (restored from old code)
  const formatLanguageCode = (code) => {
    if (!code) return 'en-us';
    const parts = code.split(/[-_]/);
    if (parts.length === 1) {
      const lang = parts[0].toLowerCase();
      if (lang === 'en') return 'en-us';
      return `${lang}-${lang}`;
    } else {
      return `${parts[0].toLowerCase()}-${parts[1].toLowerCase()}`;
    }
  };

  const formatLanguageCodeForDatabase = (code) => {
    if (!code) return 'en-US';
    const parts = code.split(/[-_]/);
    if (parts.length === 1) {
      const lang = parts[0].toLowerCase();
      if (lang === 'en') return 'en-US';
      return `${lang}-${lang.toUpperCase()}`;
    } else {
      return `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`;
    }
  };

  const escapeSql = (str) => {
      if (str === null || typeof str === 'undefined') return '';
      return String(str).replace(/'/g, "''");
  };

  // FIXED sanitizeItineraryName function
  const sanitizeItineraryName = (name) => {
    if (!name) return ''; // Return empty string if name is not provided
 
    const sanitized = name
      // Normalize the string to separate base characters from diacritical marks
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // e.g., "Héritage" -> "Heritage"
      .toLowerCase();
 
    return sanitized
      // Replace spaces, apostrophes, hyphens, and other common delimiters with a single underscore
      .replace(/[\s'-]+/g, '_') 
      // Remove any remaining characters that are not alphanumeric or an underscore
      .replace(/[^a-z0-9_]/g, '')
      // Collapse any potential multiple underscores into one (as a safeguard)
      .replace(/_+/g, '_')
      // Remove any leading or trailing underscores
      .replace(/^_|_$/g, '');
  };

  const generateSqlCommand = () => {
    const validationError = validateMetadata();
    if (validationError) {
      setStatus('error');
      setMessage(validationError);
      return;
    }
    
    try {
      const itineraryId = itinerary.metadata.id || Date.now();
      console.log('itinerary metadata:', itinerary.metadata);
      console.log('itineraryId:', itineraryId);
      const tagsJson = JSON.stringify(itinerary.metadata.tags || []);
      const latitude = parseFloat(itinerary.metadata.coordinates?.latitude || 0);
      const longitude = parseFloat(itinerary.metadata.coordinates?.longitude || 0);
      const url = publicUrl || getItineraryPublicUrl(itinerary.metadata.country, itinerary.metadata.name, itinerary.metadata.filename, itinerary.metadata.language);
      const countryCode = getCountryCode(itinerary.metadata.country);
      const locality = itinerary.metadata.city || '';
      let languageCode = formatLanguageCodeForDatabase(itinerary.metadata.language);
      const ratingValue = itinerary.metadata.rating !== null && itinerary.metadata.rating !== undefined ? parseFloat(itinerary.metadata.rating) : 0;
      
      // Get parent_guide_id from metadata (use the guide's own ID if no parent is specified)
      const parentGuideId = itinerary.metadata.parent_guide_id || itineraryId;

      const sql = `INSERT INTO \`itineraries_catalog\`
      (\`ID\`, \`Name\`, \`Category_Label\`, \`Latitude\`, \`Longitude\`, \`Country\`, \`Locality\`, \`Website\`, \`Tags\`, \`Photo\`, \`Description\`, \`Language\`, \`Rating\`, \`parent_guide_id\`)
      VALUES
      ('${itineraryId}',
      '${escapeSql(itinerary.metadata.name)}',
      'itinerary',
      ${latitude},
      ${longitude},
      '${escapeSql(countryCode)}',
      '${escapeSql(locality)}',
      '${escapeSql(url)}',
      '${escapeSql(tagsJson)}',
      '${escapeSql(itinerary.metadata.thumbnailUrl || '')}',
      '${escapeSql(itinerary.metadata.description || '')}',
      '${escapeSql(languageCode)}',
      ${ratingValue},
      '${escapeSql(parentGuideId)}')
      ON DUPLICATE KEY UPDATE
      \`Name\` = '${escapeSql(itinerary.metadata.name)}',
      \`Latitude\` = ${latitude},
      \`Longitude\` = ${longitude},
      \`Country\` = '${escapeSql(countryCode)}',
      \`Locality\` = '${escapeSql(locality)}',
      \`Website\` = '${escapeSql(url)}',
      \`Tags\` = '${escapeSql(tagsJson)}',
      \`Photo\` = '${escapeSql(itinerary.metadata.thumbnailUrl || '')}',
      \`Description\` = '${escapeSql(itinerary.metadata.description || '')}',
      \`Language\` = '${escapeSql(languageCode)}',
      \`Rating\` = ${ratingValue},
      \`parent_guide_id\` = '${escapeSql(parentGuideId)}';`;

      // Generate guide_path for itinerary_stops
      const guidePath = getStoragePathComponents(itinerary.metadata).fullPath;

      let stopsSql = '';
      if (itinerary.points && itinerary.points.length > 0) {
        itinerary.points.forEach(point => {
          if (getPointType(point) === 'popup_stop') {
            return;
          }
          const stopId = point.id;
          const name = escapeSql(point.name);
          const description = escapeSql(point.description);
          const subtitle = description;
          const categoryLabel = 'audioguide_stop';
          const category = escapeSql(point.category);
          const pointLatitude = parseFloat(point.coordinates?.latitude || 0);
          const pointLongitude = parseFloat(point.coordinates?.longitude || 0);
          const photoUrl = escapeSql(point.photoUrl);
          const audioUrl = escapeSql(point.audioUrl);
          const tier = escapeSql(point.tier || itinerary.metadata.tier || 'free');
          let stopLanguageCode = formatLanguageCodeForDatabase(point.language);

          stopsSql += `INSERT INTO \`itinerary_stops\` (\`id\`, \`Itinerary_ID\`, \`Name\`, \`Subtitle\`, \`Category_Label\`, \`Category\`, \`Latitude\`, \`Longitude\`, \`Description\`, \`Photo_Url\`, \`Audio_Url\`, \`Tier\`, \`Language\`, \`guide_path\`) VALUES ('${stopId}', '${itineraryId}', '${name}', '${subtitle}', '${categoryLabel}', '${category}', ${pointLatitude}, ${pointLongitude}, '${description}', '${photoUrl}', '${audioUrl}', '${tier}', '${stopLanguageCode}', '${escapeSql(guidePath)}')
ON DUPLICATE KEY UPDATE
\`Name\` = '${name}',
\`Subtitle\` = '${subtitle}',
\`Latitude\` = ${pointLatitude},
\`Longitude\` = ${pointLongitude},
\`Description\` = '${description}',
\`Photo_Url\` = '${photoUrl}',
\`Audio_Url\` = '${audioUrl}',
\`Tier\` = '${tier}',
\`Language\` = '${stopLanguageCode}',
\`guide_path\` = '${escapeSql(guidePath)}';\n`;
        });
      }

      // Generate QR code SQL commands
      let qrSqlCommands = '';
      const guideQrCode = escapeSql(itinerary.metadata.parent_guide_id || itineraryId);
      const guideQrUrl = `https://guidesofia.com/qr/${guideQrCode}`;
      const guideTier = escapeSql(itinerary.metadata.tier || 'free');
      const guideName = escapeSql(itinerary.metadata.name);
      const guideDescription = escapeSql(itinerary.metadata.description || 'Audio guide.');
      const guideLat = itinerary.metadata.coordinates?.latitude ? parseFloat(itinerary.metadata.coordinates.latitude) : 'NULL';
      const guideLng = itinerary.metadata.coordinates?.longitude ? parseFloat(itinerary.metadata.coordinates.longitude) : 'NULL';

      if (guideQrCode) {
        qrSqlCommands += `
INSERT INTO \`qr_codes\` (\`qr_code\`, \`qr_type\`, \`full_qr_code\`, \`guide_path\`, \`stop_id\`, \`tier\`, \`created_by\`, \`is_active\`, \`guide_name\`, \`stop_name\`, \`description\`, \`location_lat\`, \`location_lng\`, \`Itinerary_ID\`)
VALUES ('${guideQrCode}', 'guide', '${guideQrUrl}', '${escapeSql(guidePath)}', NULL, '${guideTier}', 'admin', 1, '${guideName}', NULL, '${guideDescription}', ${guideLat}, ${guideLng}, '${itineraryId}')
ON DUPLICATE KEY UPDATE
 \`qr_type\` = VALUES(\`qr_type\`),
 \`full_qr_code\` = VALUES(\`full_qr_code\`),
 \`guide_path\` = VALUES(\`guide_path\`),
 \`stop_id\` = VALUES(\`stop_id\`),
 \`tier\` = VALUES(\`tier\`),
 \`guide_name\` = VALUES(\`guide_name\`),
 \`stop_name\` = VALUES(\`stop_name\`),
 \`description\` = VALUES(\`description\`),
 \`location_lat\` = VALUES(\`location_lat\`),
 \`location_lng\` = VALUES(\`location_lng\`),
 \`Itinerary_ID\` = VALUES(\`Itinerary_ID\`);
`;
      }

      if (itinerary.points && itinerary.points.length > 0) {
        itinerary.points.forEach(point => {
          if (getPointType(point) === 'popup_stop') {
            return;
          }
          const stop_id = point.id;
          const qr_code = stop_id;
          const full_qr_code = `https://guidesofia.com/qr/${qr_code}`;
          const qr_type = getPointType(point) === 'popup_stop' ? 'popup_stop' : 'stop';
          const tier = escapeSql(point.tier || itinerary.metadata.tier || 'free');
          const guide_name = escapeSql(itinerary.metadata.name);
          const stop_name = escapeSql(point.name);
          const description = escapeSql(point.description || 'Audio guide stop.');
          const location_lat = point.coordinates?.latitude ? parseFloat(point.coordinates.latitude) : 'NULL';
          const location_lng = point.coordinates?.longitude ? parseFloat(point.coordinates.longitude) : 'NULL';

          const qrSql = `
INSERT INTO \`qr_codes\` (\`qr_code\`, \`qr_type\`, \`full_qr_code\`, \`guide_path\`, \`stop_id\`, \`tier\`, \`created_by\`, \`is_active\`, \`guide_name\`, \`stop_name\`, \`description\`, \`location_lat\`, \`location_lng\`, \`Itinerary_ID\`)
VALUES ('${qr_code}', '${qr_type}', '${full_qr_code}', '${escapeSql(guidePath)}', '${stop_id}', '${tier}', 'admin', 1, '${guide_name}', '${stop_name}', '${description}', ${location_lat}, ${location_lng}, '${itineraryId}')
ON DUPLICATE KEY UPDATE
 \`qr_type\` = VALUES(\`qr_type\`),
 \`full_qr_code\` = VALUES(\`full_qr_code\`),
 \`guide_path\` = VALUES(\`guide_path\`),
 \`stop_id\` = VALUES(\`stop_id\`),
 \`tier\` = VALUES(\`tier\`),
 \`guide_name\` = VALUES(\`guide_name\`),
 \`stop_name\` = VALUES(\`stop_name\`),
 \`description\` = VALUES(\`description\`),
 \`location_lat\` = VALUES(\`location_lat\`),
 \`location_lng\` = VALUES(\`location_lng\`),
 \`Itinerary_ID\` = VALUES(\`Itinerary_ID\`);
`;
          console.log('QR SQL for point:', point.id, qrSql);
          qrSqlCommands += qrSql;
        });
      }

      const fullSql = sql + '\n\n' + stopsSql + '\n\n' + qrSqlCommands;
      
      console.log('Generated QR SQL commands:', qrSqlCommands);
      setSqlCommand(fullSql);
      setShowSqlDialog(true);
    } catch (error) {
      console.error('Error generating SQL:', error);
      setStatus('error');
      setMessage('Error generating SQL command');
    }
  };

 
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mt-6 pt-6 border-t">
      <h3 className="text-lg font-medium mb-2 flex items-center gap-2">
        <Save className="w-5 h-5" />
        Firebase Storage & Database Export
      </h3>
      
      <div className="mb-4 bg-blue-50 p-3 rounded border border-blue-200">
        <p className="text-sm text-blue-700 mb-1">
          <strong>All fields marked with <span className="text-red-500">*</span> are required</strong> before saving to Firebase or generating SQL.
        </p>
        <p className="text-xs text-blue-600">
          This includes: Itinerary Name, Theme, Country, City, Coordinates, Duration, Difficulty, Description, Categories, Tags, and Thumbnail Image URL.
        </p>
      </div>
      
      {isUpdateMode && (
        <div className="mb-4 bg-blue-50 p-3 rounded border border-blue-200">
          <p className="flex items-center gap-2 text-blue-700">
            <History className="w-4 h-4" />
            Found existing itinerary: <strong>{itinerary.metadata.name}</strong> in <strong>{itinerary.metadata.country}</strong>
          </p>
          <p className="text-sm text-blue-600 mt-1">
            Your changes will update the existing itinerary by overwriting the current version.
          </p>
        </div>
      )}
      
      {isCheckingPrevious && (
        <div className="mb-4 bg-gray-50 p-3 rounded border border-gray-200 flex items-center gap-2">
          <Loader className="w-4 h-4 animate-spin" />
          <span>Checking for existing itineraries...</span>
        </div>
      )}
      
      <div className="flex flex-wrap gap-2">
        <FeatureGuard featureName="firebaseExport" requiredAccess="view_only">
          <button
            onClick={() => handleSaveToFirebase()}
            disabled={isLoading || (getFieldAccess('firebaseExport') !== 'edit' && getFieldAccess('firebaseExport') !== 'full')}
            className={`px-4 py-2 rounded flex items-center gap-2 ${
              getFieldAccess('firebaseExport') === 'edit' || getFieldAccess('firebaseExport') === 'full'
                ? 'bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-400'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            title={!(getFieldAccess('firebaseExport') === 'edit' || getFieldAccess('firebaseExport') === 'full') ? "Insufficient permissions to save to Firebase" : ""}
          >
            {isLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isUpdateMode ? 'Update in Firebase' : 'Save to Firebase'}
          </button>
        </FeatureGuard>

        <FeatureGuard featureName="urlGeneration" requiredAccess="view_only">
          <button
            onClick={showPublicUrl}
            disabled={isLoading || (!itinerary.metadata.country || !itinerary.metadata.name) || (getFieldAccess('urlGeneration') !== 'edit' && getFieldAccess('urlGeneration') !== 'full')}
            className={`px-4 py-2 rounded flex items-center gap-2 ${
              getFieldAccess('urlGeneration') === 'edit' || getFieldAccess('urlGeneration') === 'full'
                ? 'bg-green-500 text-white hover:bg-green-600 disabled:bg-gray-400'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            title={!(getFieldAccess('urlGeneration') === 'edit' || getFieldAccess('urlGeneration') === 'full') ? "Insufficient permissions to generate URLs" : ""}
          >
            <Link className="w-4 h-4" />
            Get URL
          </button>
        </FeatureGuard>


      </div>

      {status === 'success' && (
        <div className="mt-2 flex items-center gap-2 text-green-600 bg-green-50 p-2 rounded">
          <Check className="w-4 h-4" />
          <span>{message}</span>
        </div>
      )}

      {status === 'error' && (
        <div className="mt-2 flex items-center gap-2 text-red-600 bg-red-50 p-2 rounded">
          <AlertCircle className="w-4 h-4" />
          <span>{message}</span>
        </div>
      )}

      {/* URL Dialog */}
      {showUrlDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4">Itinerary Public URL</h3>
            {publicUrl && publicUrl.indexOf('[timestamp]') === -1 ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Your itinerary is available at the URL below. The file will be overwritten each time you save updates.
                </p>
                <div className="flex gap-2 mb-4">
                  <input type="text" value={publicUrl} readOnly className="flex-1 p-2 border rounded bg-gray-50" />
                  <button onClick={() => copyToClipboard(publicUrl)} className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="flex justify-between">
                  <button onClick={() => window.open(publicUrl, '_blank')} className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">
                    Open in Browser
                  </button>
                  <button onClick={() => setShowUrlDialog(false)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  This is a preview of the URL. To get the actual URL, please save your itinerary first.
                </p>
                {publicUrl && (
                  <div className="flex gap-2 mb-4">
                    <input type="text" value={publicUrl} readOnly className="flex-1 p-2 border rounded bg-gray-50 text-gray-400" />
                  </div>
                )}
                <div className="bg-amber-50 p-3 rounded border border-amber-200 mb-4">
                  <p className="text-sm text-amber-700 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Each itinerary will be saved as "itinerary.json" - updates will overwrite the previous version.
                  </p>
                </div>
                <div className="flex justify-between">
                  <button onClick={() => handleSaveToFirebase()} disabled={isLoading} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 flex items-center gap-2">
                    {isLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save Now
                  </button>
                  <button onClick={() => setShowUrlDialog(false)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      {/* SQL Command Dialog */}
      {showSqlDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl h-[500px] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <h3 className="text-lg font-semibold">SQL Insert Command</h3>
              <button
                onClick={() => setShowSqlDialog(false)}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold w-8 h-8 flex items-center justify-center"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 p-4 overflow-hidden">
              <p className="text-sm text-gray-600 mb-3">
                Use this SQL to add/update the itinerary in <code>itineraries_catalog</code> and its stops in <code>itinerary_stops</code>.
              </p>

              {/* SQL Display */}
              <div className="bg-gray-800 text-green-400 rounded h-full overflow-auto p-3">
                <pre className="text-sm whitespace-pre-wrap">{sqlCommand}</pre>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => copyToClipboard(sqlCommand)}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-2"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy SQL'}
              </button>
              <button
                onClick={() => setShowSqlDialog(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FirebaseExporterSection;

